import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { AppModule } from './app/app.module';

/**
 * Swaps the `index.html` loading screen for a retry prompt.
 *
 * A rejected bootstrap otherwise leaves `#loader-main` spinning with nothing on the page to act
 * on — the app is dead but still looks like it is working. Plain DOM calls on purpose: Angular
 * is by definition not running by the time this is reachable.
 */
function showBootstrapFailure(): void {
  const box = document.querySelector('#loader-main .application-loading-box');
  if (!box) return;

  box.innerHTML =
    '<p>Remmi could not finish loading.</p>' +
    '<button type="button" id="loader-retry" style="margin-top:8px;padding:8px 20px;border:none;' +
    'border-radius:20px;background:#229276;color:#fff;font-size:14px;cursor:pointer">Reload</button>';

  document.getElementById('loader-retry')?.addEventListener('click', () => window.location.reload());
}

function bootstrap() {
  platformBrowserDynamic()
    .bootstrapModule(AppModule)
    .catch(err => {
      console.error(err);
      showBootstrapFailure();
    });
}

/**
 * Wait only while the parser is still running.
 *
 * This used to be `readyState === 'complete' ? bootstrap() : addEventListener('DOMContentLoaded')`,
 * which routes every *interactive* document down the listener path. `interactive` is set once
 * parsing finishes and stays set until `load`, so DOMContentLoaded has often already fired by the
 * time this module executes — a module script whose dependency graph resolves late, or a service
 * worker that delays the response, both land here. Registering for an event that has already
 * fired means the listener never runs, `bootstrap()` is never called, and the page sits on the
 * loading screen forever; reloading re-runs the race and usually wins it, which is why a refresh
 * "fixed" it.
 *
 * `loading` is the only state where waiting is correct. In `interactive` and `complete` the DOM is
 * fully parsed, so `<app-root>` exists and it is safe to boot straight away.
 */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
} else {
  bootstrap();
}
